import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import icp_example from "../../../../../assets/img/icp_example.png";
import { FileUploadFilter } from "../../../../../components/filters/Whitespace/FileUploadFilter";
import { FileUploadFilterMode } from "../../../../../components/filters/Whitespace/types";
import { useWorkspaceStore } from "../../../../../store/workspace/WorkspaceStore";
import { CompanyMatchUploadResponse } from "../../../../../types/companyMatch";
import { useCompanyMatchSync } from "../../../../Workspace/UserFilters/useCompanyMatchSync";
import { ICPAnalysisMethod } from "../../../types";
import { ICPInputActions } from "../ICPInputActions";
import { ICPCustomerListTemplate } from "./ICPCustomerListTemplate";
import { getLoggedUserProfile } from "../../../../../utils/auth";

interface ICPCustomerListUploadInputProps {
  onViewAnalysis: () => void;
}

function CustomerListUploadInstructions() {
  return (
    <>
      <Stack gap={1} display="flex">
        <Typography variant="body1" color="text.secondary" fontWeight="bold">
          Analysis Options:
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <strong>Standard Analysis</strong> Upload a CSV of your best customers
          or successful accounts. We'll analyze the common attributes among
          these companies to help you find similar prospects. At least 200
          matched companies required.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <strong>Advanced Analysis (Recommended)</strong> Upload a CSV that
          includes both won and lost opportunities for more precise insights.{" "}
          <br />
          Add a "win_loss" column to your file: <br />
          &nbsp;&nbsp;&nbsp;&nbsp;• Mark successful customers as TRUE <br />
          &nbsp;&nbsp;&nbsp;&nbsp;• Mark lost opportunities as FALSE <br />
          This approach identifies what differentiates your wins from losses,
          providing stronger predictive insights for targeting. At least 100
          matched companies with win/loss status required.
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          fontWeight="bold"
          paddingTop={1}
        >
          File Requirements:
        </Typography>
        <Typography variant="body1" color="text.secondary">
          To maximize matching accuracy, please format your column headers
          exactly as shown below (order doesn't matter, not case sensitive)
        </Typography>
        <img alt={"column-headers"} width="100%" src={icp_example} />
        <Typography variant="body1" color="text.secondary">
          The quality of your analysis depends on the quality of data you
          provide. Including as many of these fields as possible will improve
          our ability to match your accounts and deliver actionable insights
        </Typography>
      </Stack>

      <ICPCustomerListTemplate />
    </>
  );
}

export function ICPCustomerListUploadInput({
  onViewAnalysis,
}: ICPCustomerListUploadInputProps) {
  const userProfile = getLoggedUserProfile();
  const icpCustomerListData = useWorkspaceStore(
    (state) => state.icpCustomerListData
  );
  const setICPCustomerListData = useWorkspaceStore(
    (state) => state.setICPCustomerListData
  );
  const saveICPToDraft = useWorkspaceStore((state) => state.saveICPToDraft);

  // Responsible for polling the company match status until it is processed
  useCompanyMatchSync({
    draftFilters: icpCustomerListData,
    setDraftFilters: setICPCustomerListData,
  });

  function clearIcpUpload() {
    setICPCustomerListData(undefined);
    saveICPToDraft();
  }

  function handleUpdateMatchData(matchData: CompanyMatchUploadResponse) {
    setICPCustomerListData({
      companyMatch: {
        ...icpCustomerListData?.companyMatch,
        fileId: matchData.fileId,
        userEmail: userProfile?.email,
        fileName: matchData.fileName,
        uploaded: matchData.uploaded,
        lastMatched: matchData.lastMatched,
        salesWins: matchData.salesWins,
        salesLosses: matchData.salesLosses,
        processedChunks: matchData.processedChunks,
        totalChunks: matchData.totalChunks,
      },
    });
    saveICPToDraft();
  }

  return (
    <Box display="flex" flexDirection="column" paddingTop={2.5} gap={2}>
      <CustomerListUploadInstructions />

      {icpCustomerListData?.companyMatch?.fileId ? (
        <Box display="flex" justifyContent="flex-end">
          <Button size="medium" variant="text" onClick={clearIcpUpload}>
            Clear
          </Button>
        </Box>
      ) : null}

      <Box>
        <FileUploadFilter
          id="icp-file-upload"
          matchData={icpCustomerListData?.companyMatch || null}
          onUpdateMatchData={handleUpdateMatchData}
          mode={FileUploadFilterMode.ICP_CUSTOMER_LIST}
        />
      </Box>
      <ICPInputActions
        analysisMethod={ICPAnalysisMethod.CUSTOMER_LIST}
        onViewAnalysis={onViewAnalysis}
      />
    </Box>
  );
}
